/*
 * Page for setting up and maintaining custom API settings.
 *
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Paper } from '@mui/material';
import { Table, TableHead, TableCell, TableBody, TableRow, TextField, Typography } from '@mui/material';

import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import AppLogo from '../ui/AppLogo';
import ResellerApiLogTable from './ResellerApiLogTable';

const abEnvironment = new AbEnvironment();
const IntegrationSetupCustomApiQuery = graphql`
query IntegrationSetupCustomApiQuery($accountId: UUID!) {
  allAccountKeyTs(condition: {accountId: $accountId}) {
    totalCount
    nodes {
      accountKeyId
      active
      keyType
      keyName
      keyValue
      lastUsed
      created
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    },
    vendorSelect: {
      width: 420,
    },
    vendorNameField: {
      width: 360,
    },
    accountSelect: {
      width: 370,
    },
    codeField: {
      width: 100,
    },
    descField: {
      minWidth: 486,
    },
    paper: {
        [theme.breakpoints.up('xs')]: {
          padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
          padding: theme.spacing(2),
        },
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(3),
        },
        textAlign: 'left',
        color: theme.palette.text.secondary,
        maxWidth: 1200,
        marginTop: 16,
    }
});

class IntegrationSetupCustomApi extends Component {

  constructor(props) {
    super(props);

    this.state = {
      expense_category_list: [],   // All expense categories
      expense_category_exp:  [],   // Non-cogs expense categories
      saasfee:               null, // SaaS app cost passed to customer.
      abfee:                 null, // AppBind fees - will default map to "bookkeeping fee".  AppBind and payment fees passed to customer.
      effectiveDate:         null,
      lookup_error:          null,
      apiKeyName:            ''
    };

    this.defaults = {
      saas: 'Online Services',
      cogs: 'Cost of Goods Sold',
      fees: 'Bank Fees'
    };
  }

  rollKey(id) {
    const { callPartnerApi } = this.props.auth;

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "manageApiToken",
      params: {
        keyId: id,
        action: 'RollKey'
      }
    };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      window.location.reload();
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  deactivateKey(id) {
    const { callPartnerApi } = this.props.auth;

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "manageApiToken",
      params: {
        keyId: id,
        action: 'DeactivateKey'
      }
    };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      window.location.reload();
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  deleteKey(id) {
    const { callPartnerApi } = this.props.auth;

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "manageApiToken",
      params: {
        keyId: id,
        action: 'DeleteKey'
      }
    };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      window.location.reload();
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  updateKeyName(id, keyName) {
    const { callPartnerApi } = this.props.auth;

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "manageApiToken",
      params: {
        keyId: id,
        action: 'UpdateKeyName',
        keyName: keyName
      }
    };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      window.location.reload();
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  generateKey(keyName) {
    const { callPartnerApi } = this.props.auth;

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "manageApiToken",
      params: {
        action: 'CreateKey',
        keyName: keyName
      }
    };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      window.location.reload();
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  render() {
    const { isAuthenticatedReseller, isAuthenticatedVendor, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if ((!isAuthenticatedReseller() && !isAuthenticatedVendor()) || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }

    let acctId = abEnvironment.getUserAttribute("a_id");

    let backText = "Settings";

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={IntegrationSetupCustomApiQuery}
        variables={{accountId: acctId}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            let backLink = 'settings';

            let accountKeyNodes = props.allAccountKeyTs.nodes;
            // Count active keys
            let addDisabled  = false;
            let addDisabled2 = false;
            
            let activeCount = 0;
            for (let i=0; i<props.allAccountKeyTs.totalCount; i++) {
              if (props.allAccountKeyTs.nodes[i].active === 1) {
                activeCount++;
              }
              
              if (activeCount>2) {
                addDisabled = true;
                break;
              }
            }
            if (addDisabled === true || this.state.apiKeyName.length === 0) {
              addDisabled2 = true;
            }
       
            return (
              <Grid container spacing={3} direction="row" justifyContent="flex-start">
                <Grid item xs={12}>
                  <PageTitle title="Manage custom integrations" showBackButton={true} name={backText} backLink={backLink} noMargin={true} history={this.props.history}/>
                </Grid>
                <Grid item xs={12}>
                  <Paper className={classes.paper}>
                    <Grid container spacing={2} direction="row" justifyContent="flex-start">
                      <Grid item>
                        <AppLogo logoPath='/logos/appbind-160t.png' appName='Custom AppBind integrations' imageHeight='48px'/>
                      </Grid>
                      <Grid item>
                        <Grid container spacing={0} direction="row" justifyContent="flex-start">
                          <Grid item xs={12}><Typography variant="h6">Custom AppBind integrations</Typography></Grid> 
                          <Grid item xs={12}><Typography variant="body2" className={classes.description}>Configure your own custom AppBind integrations using the AppBind API</Typography></Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper className={classes.paper}>
                          <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="flex-end">
                            <Grid item xs={12}>
                              <Grid container spacing={0} direction="row" justifyContent="flex-start">
                                <Grid item xs={12}>
                                  <Typography align="left" variant="h6">API Keys</Typography>
                                </Grid>                          
                                <Grid item xs={12}>
                                  <Typography variant="body2">API keys allow you to authenticate API requests.</Typography>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12}>
                              <Table>
                                <TableHead style={AppBindStyles.tableHead}>
                                  <TableRow style={AppBindStyles.tableHeadRow}>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Key</TableCell>
                                    <TableCell>Last used</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell/>
                                    <TableCell/>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {accountKeyNodes.map(n => {
                                    if (n.active === 1) {
                                      return(
                                        <TableRow key={n.accountKeyId}>
                                          <TableCell>{n.keyName}</TableCell>
                                          <TableCell>{n.keyValue}</TableCell>
                                          <TableCell>{n.lastUsed}</TableCell>
                                          <TableCell>{n.created}</TableCell>
                                          <TableCell>
                                            <Button color="primary" variant="outlined" 
                                              onClick={()=> { this.rollKey(n.accountKeyId) }}>
                                              Roll key
                                            </Button>
                                          </TableCell>
                                          {n.lastUsed != null &&
                                          <TableCell>
                                            <Button color="primary" variant="outlined" 
                                              onClick={()=> { this.deactivateKey(n.accountKeyId) }}>
                                              Deactivate key
                                            </Button>
                                          </TableCell>}
                                          {n.lastUsed == null &&
                                          <TableCell>
                                            <Button color="primary" variant="outlined" 
                                              onClick={()=> { this.deleteKey(n.accountKeyId) }}>
                                              Delete key
                                            </Button>
                                          </TableCell>}
                                        </TableRow>
                                      );
                                    } else {
                                      return null;
                                    }
                                  })}
                                </TableBody>

                              </Table>
                            </Grid>

                            <Grid item xs={12}/>
                            <Grid item>
                              <TextField
                                id="api-key-name" label="API key name"
                                value={this.state.apiKeyName}
                                onChange={(e) => this.setState({ apiKeyName: e.target.value })}
                                inputProps={{ maxLength: 64 }}
                                disabled={addDisabled}
                              />
                            </Grid>
                            <Grid item>
                              <Button color="primary" variant="contained" disabled={addDisabled2}
                                      onClick={()=> { this.generateKey(this.state.apiKeyName) }}>
                                Generate new key
                              </Button>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper className={classes.paper}>
                          <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="flex-end">
                            <Grid item xs={12}>
                              <Grid container spacing={0} direction="row" justifyContent="flex-start">
                                <Grid item xs={12}>
                                  <Typography align="left" variant="h6">API Logs</Typography>
                                </Grid>                          
                                <Grid item xs={12}>
                                  <Typography variant="body2">View API request logs</Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12}>
                              <ResellerApiLogTable auth={this.props.auth} {...this.props}/>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            );
          }
          return (
            <LoadingSpinner />
          );
        }}
      />
    );
  }
}

IntegrationSetupCustomApi.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(IntegrationSetupCustomApi);
